import AdminAIOrb from "./AdminAIOrb";
import AISparkIcon from "./AISparkIcon";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FaMinus, FaPlus, FaTimes, FaPaperPlane, FaStop } from "react-icons/fa";
import AIReply from "./AIReply";
import "../styles/AdminAIChat.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const storageKey = () => `admin-ai:${localStorage.getItem("userId") || "current"}`;
function restore() {
  try { return JSON.parse(sessionStorage.getItem(storageKey()) || "{}"); }
  catch { return {}; }
}

export default function AdminAIChat() {
  const { pathname } = useLocation();
  const [, refreshSession] = useState(() => { sessionStorage.removeItem("admin-ai-orb"); return 0; });
  useEffect(() => {
    const refresh = () => refreshSession((value) => value + 1);
    window.addEventListener("authSessionChanged", refresh);
    window.addEventListener("storage", refresh);
    return () => { window.removeEventListener("authSessionChanged", refresh); window.removeEventListener("storage", refresh); };
  }, []);
  const userId = localStorage.getItem("userId");
  const publicPage = /^\/(?:login\/?|forgot-password\/?|reset-password(?:\/.*)?|reference-check(?:\/.*)?)?$/.test(pathname);
  if (publicPage || !localStorage.getItem("token") || Number(localStorage.getItem("userRole")) !== 1) return null;
  return <FloatingChat key={userId} />;
}

function FloatingChat() {
  const [saved] = useState(restore);
  const [open, setOpen] = useState(() => window.innerWidth <= 768 ? saved.mobileEnabled === true : true);
  const [closing, setClosing] = useState(false);
  const [minimized, setMinimized] = useState(true);
  const [messages, setMessages] = useState(saved.messages || []);
  const [position, setPosition] = useState(saved.position || { x: Math.max(8, window.innerWidth - 438), y: 100 });
  const [size, setSize] = useState(() => ({
    width: Number.isFinite(saved.size?.width) ? saved.size.width : 420,
    height: Number.isFinite(saved.size?.height) ? saved.size.height : 640,
  }));
  const resizing = useRef(null);
  const [pendingLookup, setPendingLookup] = useState(["details", "expiry"].includes(saved.pendingLookup) ? saved.pendingLookup : null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const panel = useRef(null);
  const drag = useRef(null);
  const bottom = useRef(null);
  const input = useRef(null);
  const request = useRef(null);

  useEffect(() => {
    const show = () => { setClosing(false); setOpen(true); setMinimized(false); };
    const resetForSession = () => {
      sessionStorage.removeItem("admin-ai-orb");
      request.current?.abort(); request.current = null;
      setBusy(false); setClosing(false); setMessages([]); setDraft(""); setError(""); setPendingLookup(null);
      setOpen(window.innerWidth > 768); setMinimized(true);
    };
    window.addEventListener("open-admin-ai", show);
    window.addEventListener("authSessionChanged", resetForSession);
    return () => { window.removeEventListener("open-admin-ai", show); window.removeEventListener("authSessionChanged", resetForSession); request.current?.abort(); };
  }, []);
  useEffect(() => {
    try { sessionStorage.setItem(storageKey(), JSON.stringify({ messages, position, size, pendingLookup, pinned: true, open, mobileEnabled: open && !closing })); } catch {}
  }, [messages, position, size, pendingLookup, open, closing]);
  useEffect(() => {
    if (open && !minimized) input.current?.focus({ preventScroll: true });
  }, [open, minimized]);
  useEffect(() => {
    const messagesArea = bottom.current?.parentElement;
    if (messagesArea) messagesArea.scrollTop = messagesArea.scrollHeight;
  }, [messages, busy, minimized]);
  useLayoutEffect(() => {
    if (!open || minimized) return undefined;
    const clamp = () => {
      // Use the destination dimensions, not the intermediate animated height.
      const width = Math.min(size.width, window.innerWidth - 16);
      const height = minimized ? (panel.current?.querySelector("header")?.offsetHeight || 68) + 2 : Math.min(size.height, window.innerHeight - 16);
      setPosition((p) => ({ x: Math.max(8, Math.min(p.x, window.innerWidth - width - 8)),
        y: Math.max(8, Math.min(p.y, window.innerHeight - height - 8)) }));
    };
    clamp();
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, [open, minimized, size.width, size.height]);

  useEffect(() => {
    if (!closing) return;
    const timer = setTimeout(() => {
      setOpen(false); setClosing(false);
      setMessages([]); setDraft(""); setError(""); setPendingLookup(null);
    }, 200);
    return () => clearTimeout(timer);
  }, [closing]);

  const startDrag = (event) => {
    if (event.target.closest("button") || event.button !== 0) return;
    drag.current = { x: event.clientX - position.x, y: event.clientY - position.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveDrag = (event) => {
    if (!drag.current || !panel.current) return;
    const rect = panel.current.getBoundingClientRect();
    setPosition({ x: Math.max(8, Math.min(event.clientX - drag.current.x, window.innerWidth - rect.width - 8)),
      y: Math.max(8, Math.min(event.clientY - drag.current.y, window.innerHeight - rect.height - 8)) });
  };
  const resizeChat = (width, height) => {
    setSize({
      width: Math.max(Math.min(320, window.innerWidth - position.x - 8), Math.min(width, window.innerWidth - position.x - 8)),
      height: Math.max(Math.min(360, window.innerHeight - position.y - 8), Math.min(height, window.innerHeight - position.y - 8)),
    });
  };
  const send = async (event, question = draft) => {
    event?.preventDefault();
    if (!question.trim() || request.current || closing) return;
    const identifier = question.trim();
    const employeeId = identifier.match(/^(?:(?:employee|staff|user)\s*)?id\s*[:#]?\s*([\w-]+)$/i)?.[1] || (/^[\w-]*\d[\w-]*$/.test(identifier) ? identifier : null);
    const target = employeeId ? `employee ID ${employeeId}` : `user ${identifier}`;
    const lookupQuestion = pendingLookup === "details" ? `Provide details for ${target}` : pendingLookup === "expiry" ? `Give me the tasks expiring within 30 days for ${target}` : identifier;
    const next = [...messages, { role: "user", content: identifier }];
    const apiMessages = [...next.slice(-19)];
    apiMessages[apiMessages.length - 1] = { role: "user", content: lookupQuestion };
    setPendingLookup(null);
    setMessages(next); setDraft(""); setError(""); setBusy(true);
    const controller = new AbortController();
    request.current = controller;
    // Start the typing pause alongside the request, so slow replies add no extra wait.
    const typingPause = new Promise((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        controller.signal.removeEventListener("abort", finish);
        resolve();
      };
      const timer = setTimeout(finish, 1500);
      controller.signal.addEventListener("abort", finish, { once: true });
    });
    try {
      const { data } = await axios.post(`${API_BASE}/api/ai/chat`, { messages: apiMessages, currentPath: window.location.pathname }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        signal: controller.signal,
        timeout: 190000,
      });
      await typingPause;
      if (controller.signal.aborted) return;
      setMessages((current) => [...current, { role: "assistant", content: data.reply }]);
    } catch (err) {
      if (!controller.signal.aborted) setError(err.response?.data?.message || "Could not reach the AI. Please try again.");
    } finally { if (request.current === controller) { request.current = null; setBusy(false); } }
  };

  const closeChat = () => {
    if (!window.confirm("Close HR Assistant and clear this chat? The floating icon will disappear. You can reopen it from Help.")) return;
    request.current?.abort();
    request.current = null;
    setBusy(false);
    setClosing(true);
  };

  const stopReply = () => {
    request.current?.abort();
    request.current = null;
    setBusy(false);
    setError("");
    input.current?.focus({ preventScroll: true });
  };

  if (!open) return null;
  if (minimized) return createPortal(<AdminAIOrb initialPosition={{ x: Math.max(16, (document.documentElement.clientWidth || window.innerWidth) - 64), y: Math.max(16, window.innerHeight - 64) }} onExpand={() => setMinimized(false)} />, document.body);
  return createPortal(
    <section ref={panel} className={`admin-ai-chat ${minimized ? "is-minimized" : ""}${closing ? " is-closing" : ""}`}
      style={{ left: position.x, top: position.y, width: Math.min(size.width, window.innerWidth - 16), height: minimized ? (panel.current?.querySelector("header")?.offsetHeight || 68) + 2 : Math.min(size.height, window.innerHeight - 16) }} role="dialog" aria-modal="false" aria-label="HR Assistant">
      <header className="admin-ai-header" onPointerDown={startDrag} onPointerMove={moveDrag}
        onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}
        onLostPointerCapture={() => { drag.current = null; }}>
        <div className="admin-ai-brand"><AISparkIcon /><div><strong>HR Assistant</strong><small>Drag here to move</small></div></div>
        <div className="admin-ai-controls">
          <button type="button" data-no-standard-button="true" aria-label={minimized ? "Expand chat" : "Minimize chat"} onClick={() => setMinimized(!minimized)}>{minimized ? <FaPlus aria-hidden="true" /> : <FaMinus aria-hidden="true" />}</button>
          <button type="button" data-no-standard-button="true" aria-label="Close chat" onClick={closeChat}><FaTimes aria-hidden="true" /></button>
        </div>
      </header>
      {!minimized && <>
        <div className="admin-ai-toolbar"><span>Ask a question while you work</span><button type="button" disabled={busy} onClick={() => { setMessages([]); setPendingLookup(null); setError(""); }}>Clear chat</button></div>
        <div className="admin-ai-messages" role="log" aria-live="polite" aria-relevant="additions text">
          {!messages.length && <div className="admin-ai-suggestions" aria-label="Suggested questions">
            <div className="admin-ai-prompt-heading"><AISparkIcon /><div><strong>Start a conversation</strong><span>Choose a prompt to get started</span></div></div>
            {[
              { label: "How do I assign a task?" },
              { label: "How do I assign training?" },
              { label: "How do I change an inactive user to a candidate with a new profile?" },
              { label: "How many candidates are in this company?" },
              { label: "Provide details for a user", lookup: "details" },
              { label: "Which tasks expire within 30 days for a user?", lookup: "expiry" },
            ].map(({ label, lookup }) => <button key={label} type="button" data-no-standard-button="true" onClick={() => {
              if (!lookup) { send(null, label); return; }
              setPendingLookup(lookup);
              setMessages((current) => [...current, { role: "user", content: label }, { role: "assistant", content: "What is the user's name or employee ID?" }]);
              setDraft("");
              input.current?.focus({ preventScroll: true });
            }}><span className="admin-ai-prompt-text">{label}</span><AISparkIcon /></button>)}
          </div>}
          {messages.map((message, index) => <div key={index} className={`admin-ai-message ${message.role}`}><strong className="admin-ai-speaker">{message.role === "user" ? "You" : "HR Assistant"}</strong>{message.role === "assistant" ? <AIReply content={message.content} /> : <div>{message.content}</div>}</div>)}
          {busy && <div className="admin-ai-message admin-ai-typing" role="status" aria-label="HR Assistant is preparing a reply">
            <strong>HR Assistant</strong>
            <div className="admin-ai-typing-dots" aria-hidden="true"><span /><span /><span /></div>
          </div>}
          {error && <p className="admin-ai-error" role="alert">{error}</p>}
          <div ref={bottom} />
        </div>
        <form className="admin-ai-composer" onSubmit={(event) => send(event)}>
          <label className="visually-hidden" htmlFor="admin-ai-question">Your question</label>
          <div className="admin-ai-input-wrap">
          <textarea id="admin-ai-question" ref={input} value={draft} maxLength={4000} rows={2} placeholder="Ask a question…" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(e); } }} />
          {busy ? <button type="button" data-no-standard-button="true" aria-label="Stop response" title="Stop response" onClick={stopReply}><FaStop aria-hidden="true" /></button>
            : <button type="submit" data-no-standard-button="true" aria-label="Send message" title="Send message" disabled={!draft.trim()}><FaPaperPlane aria-hidden="true" /></button>}
          </div>
        </form>
        <button type="button" data-no-standard-button="true" className="admin-ai-resize" aria-label="Resize chat" title="Drag to resize, or use arrow keys"
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            const rect = panel.current.getBoundingClientRect();
            resizing.current = { x: event.clientX, y: event.clientY, width: rect.width, height: rect.height };
            event.currentTarget.setPointerCapture(event.pointerId);
            event.preventDefault();
          }}
          onPointerMove={(event) => {
            const origin = resizing.current;
            if (origin) resizeChat(origin.width + event.clientX - origin.x, origin.height + event.clientY - origin.y);
          }}
          onPointerUp={() => { resizing.current = null; }} onPointerCancel={() => { resizing.current = null; }} onLostPointerCapture={() => { resizing.current = null; }}
          onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
            event.preventDefault();
            const rect = panel.current.getBoundingClientRect();
            resizeChat(rect.width + (event.key === "ArrowRight" ? 20 : event.key === "ArrowLeft" ? -20 : 0), rect.height + (event.key === "ArrowDown" ? 20 : event.key === "ArrowUp" ? -20 : 0));
          }}><span aria-hidden="true" /></button>
      </>}
    </section>, document.body,
  );
}
